import React, { useEffect, useState } from 'react';
import { Modal, Form, Select, InputNumber, message } from 'antd';
import { getCommonOptions } from '@/api/common';
import { giveVip } from '@/api/order';

interface GiveVipDialogProps {
  open: boolean;
  userId: string;
  userName?: string;
  onClose: () => void;
  onSuccess?: () => void;
}

const GiveVipDialog: React.FC<GiveVipDialogProps> = ({
  open,
  userId,
  userName,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [vipLevels, setVipLevels] = useState<{ value: number; text: string }[]>([]);

  // 获取会员等级选项
  useEffect(() => {
    if (open) {
      const fetchVipLevels = async () => {
        try {
          const response = await getCommonOptions();
          if (response.status === 200 && response.data?.appleSubscribeVIPLevels) {
            setVipLevels(response.data.appleSubscribeVIPLevels);
          }
        } catch (error) {
          console.error('获取会员等级选项失败:', error);
          message.error('获取会员等级选项失败');
        }
      };
      fetchVipLevels();
    }
  }, [open]);

  // 重置表单
  useEffect(() => {
    if (open) {
      form.resetFields();
    }
  }, [open, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      
      const response = await giveVip({
        level: values.level,
        days: values.days,
        userId: userId,
        deviceType: values.deviceType,
      });

      if (response.status === 200) {
        message.success('赠送会员成功');
        onClose();
        onSuccess?.();
      } else {
        message.error(response.message || '赠送会员失败');
      }
    } catch (error: any) {
      if (error.errorFields) {
        // 表单验证错误
        return;
      }
      message.error(error.message || '赠送会员失败');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title="赠送会员"
      open={open}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={loading}
      okText="确定"
      cancelText="取消"
    >
      <Form
        form={form}
        layout="vertical"
        style={{ marginTop: 20 }}
      >
        {userName && (
          <Form.Item label="用户">
            <span>{userName}</span>
          </Form.Item>
        )}
        
        <Form.Item
          name="level"
          label="会员等级"
          rules={[{ required: true, message: '请选择会员等级' }]}
        >
          <Select
            placeholder="请选择会员等级"
            options={vipLevels.map(item => ({
              label: item.text,
              value: item.value,
            }))}
          />
        </Form.Item>

        <Form.Item
          name="deviceType"
          label="设备类型"
          rules={[{ required: true, message: '请选择设备类型' }]}
        >
          <Select
            placeholder="请选择设备类型"
            options={[
              { label: 'iOS', value: 0 },
              { label: 'Android', value: 1 },
            ]}
          />
        </Form.Item>

        <Form.Item
          name="days"
          label="赠送时长（天）"
          rules={[
            { required: true, message: '请输入赠送时长' },
            { type: 'number', min: 1, message: '赠送时长至少为1天' },
          ]}
        >
          <InputNumber
            placeholder="请输入赠送时长（天）"
            min={1}
            precision={0}
            style={{ width: '100%' }}
            parser={(value) => value?.replace(/\D/g, '') || ''}
            formatter={(value) => value?.replace(/\D/g, '') || ''}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default GiveVipDialog;

